/**
 * @module   public/js/services/dialers/twilioDialer.js
 * @summary  Twilio implementation of the Base Dialer contract with client-side state.
 *
 * @description
 * A robust wrapper around the tenant-scoped Twilio callable endpoints.  In
 * addition to launching calls it now keeps an internal finite-state machine
 * (`idle → dialing → ringing → connected → ended|error`), exposes listener
 * hooks, and records the most recently dialled number so UI layers can offer
 * “redial” affordances.  When Twilio’s Device or Connection object is wired up
 * client-side the dialer can transition itself into the `connected` state and
 * fall back to device-side DTMF if the backend helper is absent.
 *
 * @example
 * ```js
 * // import this module from the local dialer directory.
 * const dialer = new TwilioDialer({ apiClient, endpoints });
 * await dialer.call({ to:"+15551234567", contactId:"42", userId:uid });
 * dialer.on('statechange', ({ state }) => console.log(state));
 * ```
 *
 * @author   Auto-Generated
 * @version  1.2.0
 * @since    2025-05-11
 */

import BaseDialer from './baseDialer.js';
import { auth } from '../../core/firebase-service.js';

/**
 * @typedef  {Object} CallOpts
 * @property {string}  to         – E.164 phone number to dial
 * @property {string=} extension  – optional DTMF extension metadata
 * @property {string=} contactId  – CRM contact ID (optional)
 * @property {string=} userId     – Firebase auth UID (optional)
 */
export default class TwilioDialer extends BaseDialer {
  /** @type {"idle"|"dialing"|"ringing"|"connected"|"ended"|"error"} */ #state =
    'idle';
  /** @type {string|null}   */ #activeCallSid = null;
  /** @type {string}        */ #lastDialed = '';
  /** @type {Map<string, Set<Function>>} */ #listeners = new Map();
  /** @type {any} */ #connection = null;
  /** @type {{ type:string, handler:Function }[]} */ #connectionHandlers = [];

  /**
   * @param {{ apiClient:Object, endpoints:Object }} deps
   */
  constructor({ apiClient, endpoints } = {}) {
    super();
    if (!apiClient || !endpoints) {
      throw new Error('TwilioDialer needs { apiClient, endpoints }');
    }
    this.apiClient = apiClient;
    this.endpoints = endpoints;
  }

  /**
   * Current dialer state.
   * @returns {"idle"|"dialing"|"ringing"|"connected"|"ended"|"error"}
   */
  getState() {
    return this.#state;
  }

  /**
   * Return the last dialled phone number.
   * @returns {string}
   */
  getLastDialed() {
    return this.#lastDialed;
  }

  /**
   * Persist the last dialled phone number.
   * @param {string} number
   */
  setLastDialed(number = '') {
    this.#lastDialed =
      typeof number === 'string' ? number : String(number ?? '');
    return this.#lastDialed;
  }

  /**
   * Subscribe to dialer events.
   * @param {"statechange"|"error"} event
   * @param {Function} handler
   * @returns {() => void}
   */
  on(event, handler) {
    if (!event || typeof handler !== 'function') return () => {};
    if (!this.#listeners.has(event)) {
      this.#listeners.set(event, new Set());
    }
    const set = this.#listeners.get(event);
    set.add(handler);
    return () => this.off(event, handler);
  }

  /**
   * Remove a listener.
   * @param {"statechange"|"error"} event
   * @param {Function} handler
   */
  off(event, handler) {
    const set = this.#listeners.get(event);
    if (!set) return;
    set.delete(handler);
    if (!set.size) this.#listeners.delete(event);
  }

  /**
   * Attach a Twilio Device/Connection so state can auto-transition on accept/disconnect.
   * @param {any} connection
   */
  attachConnection(connection) {
    this.#teardownConnection();
    if (!connection) {
      this.#connection = null;
      return;
    }
    this.#connection = connection;

    const bind = (type, handler) => {
      if (typeof connection.on === 'function') {
        connection.on(type, handler);
        this.#connectionHandlers.push({ type, handler });
      } else if (typeof connection.addListener === 'function') {
        connection.addListener(type, handler);
        this.#connectionHandlers.push({ type, handler });
      }
    };

    bind('accept', () => {
      const sid =
        this.#activeCallSid ||
        connection?.parameters?.CallSid ||
        connection?.parameters?.callSid ||
        connection?.callSid;
      if (sid) this.#activeCallSid = sid;
      this.#transition('connected', { callSid: this.#activeCallSid });
    });

    bind('connect', () => {
      const sid =
        this.#activeCallSid ||
        connection?.parameters?.CallSid ||
        connection?.parameters?.callSid ||
        connection?.callSid;
      if (sid) this.#activeCallSid = sid;
      this.#transition('connected', { callSid: this.#activeCallSid });
    });

    bind('disconnect', () => {
      this.#transition('ended', { reason: 'device_disconnect' });
      this.#teardownConnection();
    });

    bind('error', (err) => {
      this.#handleError('device_error', err);
    });
  }

  /** @override */
  async call(
    /** @type {CallOpts} */ {
      to,
      extension = '',
      contactId = '',
      userId = '',
    } = {}
  ) {
    if (!to) throw new Error('TwilioDialer.call → missing `to` number');

    this.setLastDialed(to);
    this.#transition('dialing', { to, extension, contactId, userId });

    const url =
      this.endpoints.initiateCall?.({ to, contactId, userId }) ??
      this.endpoints.initiateCall();
    const payload = { to, contactId, userId };
    if (extension) payload.extension = extension;

    try {
      const res = await this.apiClient.fetchJson(url, {
        auth: true,
        body: payload,
      });

      if (res?.ok === false) {
        throw new Error(res?.error || 'TwilioDialer: backend reported failure');
      }

      const callSid =
        res?.callSid ||
        res?.data?.callSid ||
        res?.data?.sid ||
        res?.sid ||
        null;

      if (callSid) {
        this.#activeCallSid = callSid;
      }

      this.#transition('ringing', {
        to,
        contactId,
        extension,
        userId,
        callSid: this.#activeCallSid,
      });
      return { callSid: this.#activeCallSid };
    } catch (err) {
      this.#handleError('call_failed', err);
      throw err;
    }
  }

  /**
   * Hang up the active call (best-effort).
   */
  async hangup() {
    const callSid = this.#activeCallSid;
    try {
      if (callSid && typeof this.endpoints.terminateCall === 'function') {
        const url = this.endpoints.terminateCall({ callSid });
        await this.apiClient.fetchJson(url, {
          auth: true,
          body: { callSid },
        });
      } else if (callSid && typeof this.endpoints.terminateCall === 'string') {
        await this.apiClient.fetchJson(this.endpoints.terminateCall, {
          auth: true,
          body: { callSid },
        });
      }
    } catch (err) {
      this.#handleError('hangup_failed', err);
      throw err;
    } finally {
      this.#teardownConnection();
      this.#transition('ended', { reason: 'hangup' });
    }
  }

  /**
   * Send DTMF digits via backend or Twilio connection fallback.
   * @param {string} digits
   */
  async sendDigits(digits) {
    const payload = typeof digits === 'string' ? digits : String(digits ?? '');
    if (!payload) return;
    const callSid = this.#activeCallSid;

    if (callSid && typeof this.endpoints.sendDigits === 'function') {
      const url = this.endpoints.sendDigits({ callSid, digits: payload });
      await this.apiClient.fetchJson(url, {
        auth: true,
        body: { callSid, digits: payload },
      });
      return;
    }

    if (callSid && typeof this.endpoints.sendDigits === 'string') {
      await this.apiClient.fetchJson(this.endpoints.sendDigits, {
        auth: true,
        body: { callSid, digits: payload },
      });
      return;
    }

    if (this.#connection?.sendDigits) {
      this.#connection.sendDigits(payload);
      return;
    }

    // eslint-disable-next-line no-console
    console.debug('[TwilioDialer] sendDigits fallback noop', {
      digits: payload,
    });
  }

  /**
   * Log an in-browser call when a real Twilio CallSid is available.
   *
   * The legacy behaviour minted a `local_<uuid>` stub before Twilio issued a
   * CallSid, which prevented webhook lookups from resolving the tenant.  This
   * variant simply forwards to `logClientCall` once a CallSid exists and
   * otherwise no-ops.
   *
   * @param {object} opts
   * @param {string=} opts.callSid
   * @param {string} opts.to
   * @param {string=} opts.contactId
   * @param {string=} opts.userId
   * @param {string=} opts.taskId
   * @param {string=} opts.extension
   * @param {string=} opts.device
   * @param {string=} opts.tenantId
   * @returns {Promise<{callSid:string}>}
   */
  async logBrowserCall({
    callSid = '',
    to = '',
    contactId = '',
    userId = '',
    taskId = '',
    extension = '',
    device = 'web',
    tenantId = '',
  } = {}) {
    if (!callSid) {
      // eslint-disable-next-line no-console
      console.warn('[TwilioDialer] logBrowserCall skipped: missing CallSid');
      return { callSid: '' };
    }

    return this.logClientCall({
      callSid,
      to,
      contactId,
      userId,
      taskId,
      extension,
      device,
      tenantId,
    });
  }

  /**
   * Log a Twilio Client (browser) call using the real Twilio CallSid.
   * @param {object} opts
   * @param {string} opts.callSid
   * @param {string=} opts.to
   * @param {string=} opts.contactId
   * @param {string=} opts.userId
   * @param {string=} opts.taskId
   * @param {string=} opts.extension
   * @param {string=} opts.device
   * @param {string=} opts.tenantId
   * @returns {Promise<{ callSid: string }>}
   */
  async logClientCall({
    callSid,
    to = '',
    contactId = '',
    userId = '',
    taskId = '',
    extension = '',
    device = 'web',
    tenantId = '',
  } = {}) {
    if (!callSid) {
      throw new Error('TwilioDialer.logClientCall → missing `callSid`');
    }

    const url =
      typeof this.endpoints.logClientCall === 'function'
        ? this.endpoints.logClientCall({ callSid })
        : this.endpoints.logClientCall;

    const tenant =
      tenantId ||
      auth.currentUser?.tenantId ||
      this.apiClient?.auth?.currentUser?.tenantId ||
      // FocusMode bootstrap stores session metadata on the global DI container.
      window?.di?.auth?.currentUser?.tenantId ||
      null;
    const payload = {
      callSid,
      to,
      contactId,
      userId,
      taskId,
      device,
      callType: 'twilio_client',
    };
    if (extension) {
      payload.extension = extension;
    }
    if (tenant) {
      payload.tenantId = tenant;
    }
    console.info('[TwilioDialer] logClientCall → request', { url, payload });
    const res = await this.apiClient.fetchJson(url, {
      auth: true,
      body: payload,
    });

    console.info('[TwilioDialer] logClientCall ← response', {
      url,
      ok: res?.ok,
      callSid: res?.callSid || callSid,
      error: res?.error || null,
    });

    if (res?.ok === false) {
      throw new Error(res?.error || 'TwilioDialer: client log failed');
    }

    const sid = res?.callSid || callSid;
    if (sid) {
      this.#activeCallSid = this.#activeCallSid || sid;
    }

    return { callSid: sid };
  }

  /**
   * Kick off a FocusMode PSTN call via the backend (enables AMD + status webhooks).
   * @param {{ to: string, contactId?: string, taskId?: string }} opts - Call initiation options.
   * @returns {Promise<{ callSid: string, conferenceName: string }>} Lead leg identifiers.
   */
  async initiateFocusModeCall({
    to = '',
    extension = '',
    contactId = '',
    taskId = '',
  } = {}) {
    if (!to) {
      throw new Error('TwilioDialer.initiateFocusModeCall → missing `to`');
    }

    const user = auth.currentUser || null;
    const tenantId = user?.tenantId || null;

    const url = this.endpoints.focusmodeInitiate({ to });
    const payload = { to, extension, contactId, taskId };
    if (tenantId) {
      payload.tenantId = tenantId;
    }
    console.info('[TwilioDialer] focusmodeInitiate → request', {
      url,
      payload,
    });
    const res = await this.apiClient.fetchJson(url, {
      auth: true,
      body: payload,
    });
    console.info('[TwilioDialer] focusmodeInitiate ← response', {
      url,
      ok: res?.ok,
      callSid: res?.callSid || null,
      conferenceName: res?.conferenceName || null,
      error: res?.error || null,
    });

    if (!res?.ok) {
      throw new Error(res?.error || 'FocusMode initiation failed');
    }

    return { callSid: res.callSid, conferenceName: res.conferenceName };
  }

  /**
   * Cancel an in-flight FocusMode PSTN call so the lead stops ringing.
   * @param {{ callSid: string }} opts - Identifier of the PSTN leg to cancel.
   * @returns {Promise<boolean>} Resolves true when cancellation succeeds.
   */
  async terminateFocusModeCall({ callSid } = {}) {
    if (!callSid) {
      throw new Error(
        'TwilioDialer.terminateFocusModeCall → missing `callSid`'
      );
    }

    const url =
      typeof this.endpoints.focusmodeTerminate === 'function'
        ? this.endpoints.focusmodeTerminate({ callSid })
        : this.endpoints.focusmodeTerminate;

    const payload = { callSid };
    console.info('[TwilioDialer] focusmodeTerminate → request', {
      url,
      payload,
    });
    const res = await this.apiClient.fetchJson(url, {
      auth: true,
      body: payload,
    });
    console.info('[TwilioDialer] focusmodeTerminate ← response', {
      url,
      ok: res?.ok,
      error: res?.error || null,
    });

    if (!res?.ok) {
      throw new Error(res?.error || 'FocusMode terminate failed');
    }

    return true;
  }

  /**
   * Emit a state transition and notify listeners.
   * @param {"idle"|"dialing"|"ringing"|"connected"|"ended"|"error"} next
   * @param {object} meta
   */
  #transition(next, meta = {}) {
    const previousSid = this.#activeCallSid;
    this.#state = next;
    if (next === 'ended' || next === 'error') {
      this.#activeCallSid = null;
    }

    this.#emit('statechange', {
      state: next,
      ...meta,
      callSid: meta.callSid ?? previousSid ?? null,
    });
  }

  /**
   * Handle errors in a consistent, structured way.
   * @param {string} code
   * @param {unknown} err
   */
  #handleError(code, err) {
    const message = err?.message || String(err);
    const callSid = this.#activeCallSid;
    this.#transition('error', { code, message, callSid });
    this.#emit('error', {
      code,
      message,
      error: err,
      callSid,
    });
  }

  /**
   * Emit an event payload to registered listeners.
   * @param {string} event
   * @param {any} payload
   */
  #emit(event, payload) {
    const listeners = this.#listeners.get(event);
    if (!listeners) return;
    listeners.forEach((handler) => {
      try {
        handler(payload);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[TwilioDialer] listener error', err);
      }
    });
  }

  /** Remove Twilio connection listeners. */
  #teardownConnection() {
    if (!this.#connection) return;
    const remove = (type, handler) => {
      if (typeof this.#connection.off === 'function') {
        this.#connection.off(type, handler);
      } else if (typeof this.#connection.removeListener === 'function') {
        this.#connection.removeListener(type, handler);
      }
    };
    this.#connectionHandlers.forEach(({ type, handler }) =>
      remove(type, handler)
    );
    this.#connectionHandlers = [];
    this.#connection = null;
  }
}
